import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import DateLabel from '../../label/DateLabel';
import DeleteIcon from '@mui/icons-material/Delete';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import {
  buildVarValues,
  extractCredentialIds,
  getDurationInDays,
  getOutOfOfficeStatus,
  resolveOutOfOfficeVars,
} from '../helpers/outOfOffice';

// Shared status vocabulary for the list and the editor header.
export function getStatusMeta(status, colors) {
  const map = {
    active: { label: 'Active', color: colors.success },
    scheduled: { label: 'Scheduled', color: colors.info },
    expired: { label: 'Expired', color: colors.money.neutral },
  };
  return map[status] || map.expired;
}

export const OutOfOfficeCard = ({
  data,
  mailboxes = [],
  isSelected,
  onSelect,
  onDelete,
  dateFormat,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const varValues = buildVarValues(data, dateFormat);
  const status = getOutOfOfficeStatus(data);
  const statusMeta = getStatusMeta(status, colors);
  const isExpired = status === 'expired';

  const credentialIds = extractCredentialIds(data);
  const selectedMailboxes = mailboxes.filter((mailbox) =>
    credentialIds.includes(mailbox.id),
  );
  const duration = getDurationInDays(data.startDate, data.endDate);

  const subject =
    resolveOutOfOfficeVars(data.subject, varValues) || t('No subject yet');

  return (
    <Box
      className="flex items-center shrink-0"
      sx={{
        '& .delete-btn-wrapper': {
          opacity: 0,
          width: 0,
          overflow: 'hidden',
          transition: 'opacity 0.2s, width 0.2s, margin 0.2s',
        },
        '&:hover .delete-btn-wrapper': {
          opacity: 1,
          width: '40px',
          marginLeft: '4px',
        },
      }}
    >
      <Button
        onClick={() => onSelect(data)}
        sx={{
          flex: 1,
          minWidth: 0,
          gap: '4px',
          alignItems: 'start',
          flexDirection: 'column',
          textAlign: 'left',
          padding: '8px 10px',
          opacity: isExpired && !isSelected ? 0.6 : 1,
          bgcolor: isSelected ? colors.info + '30' : colors.card,
          // Status accent on the left edge, kept through the hover state.
          border: `1px solid ${isSelected ? colors.info + '80' : 'transparent'}`,
          borderLeftWidth: '3px',
          borderLeftColor: statusMeta.color,
          ':hover': {
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
            borderLeftWidth: '3px',
            borderLeftColor: statusMeta.color,
          },
        }}
      >
        <StatusPill status={status} statusMeta={statusMeta} />

        <Typography fontWeight={600} fontSize={13} className="w-full" noWrap>
          {subject}
        </Typography>

        <Box className="flex items-center gap-1 opacity-70">
          <Typography fontSize={11} fontWeight={500}>
            <DateLabel date={data.startDate} />
          </Typography>
          <ArrowRightAltIcon sx={{ fontSize: '14px' }} />
          <Typography fontSize={11} fontWeight={500}>
            <DateLabel date={data.endDate} />
          </Typography>
          {duration && (
            <Typography fontSize={11} className="opacity-70">
              {`· ${duration} ${duration === 1 ? t('day') : t('days')}`}
            </Typography>
          )}
        </Box>

        <Box className="flex items-center gap-1 opacity-70 w-full min-w-0">
          <MailOutlineIcon sx={{ fontSize: '13px' }} />
          <Typography fontSize={11} noWrap>
            {selectedMailboxes.length
              ? selectedMailboxes.map((mailbox) => mailbox.label).join(', ')
              : t('No mailbox selected')}
          </Typography>
        </Box>
      </Button>

      <Box className="delete-btn-wrapper flex items-center">
        <Tooltip title={t('Delete notification')}>
          <IconButton
            size="small"
            onClick={() => onDelete(data)}
            sx={{ color: colors.error }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const StatusPill = ({ status, statusMeta }) => {
  const { t } = useTranslation();
  const isActive = status === 'active';

  return (
    <Box
      className="flex items-center gap-1 rounded-full px-1.5"
      sx={{
        bgcolor: statusMeta.color + '30',
        border: `1px solid ${statusMeta.color}60`,
      }}
    >
      <Box
        sx={{
          width: '6px',
          height: '6px',
          borderRadius: '9999px',
          bgcolor: statusMeta.color,
          // A quiet pulse marks the notification that is answering right now.
          '@keyframes ooo-pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.25 },
          },
          animation: isActive ? 'ooo-pulse 2s ease-in-out infinite' : 'none',
        }}
      />
      <Typography
        fontSize={10}
        fontWeight={700}
        sx={{ color: statusMeta.color, textTransform: 'uppercase' }}
      >
        {t(statusMeta.label)}
      </Typography>
    </Box>
  );
};
